import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import GithubIcon from '@/assets/images/commuity-github.svg?react'
import SuccessIcon from '@/assets/images/success.svg?react'
import LogoIcon from '@/assets/images/symbol-gata.svg?react'
import { Button, Input, Trans } from '@/components'
import useOauth, { OauthType } from '@/hooks/use-oauth'
import { useUserStore } from '@/stores/user'

enum Step {
  Landing,
  SetPassword,
  Success,
}

export function RegisterPage() {
  const [step, setStep] = useState(Step.Landing)

  const [loading, setLoading] = useState(false)

  const [password, setPassword] = useState('')
  const [rePassword, setRePassword] = useState('')

  const { oauthCode, oauthType, GoogleLoginButton } = useOauth()

  const userStore = useUserStore()

  const location = useLocation()

  const [inviterCode, setInviterCode] = useState(() => {
    const searchParams = new URLSearchParams(location.search)
    return searchParams.get('inviter_code') || ''
  })

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlInviterCode = searchParams.get('inviter_code')

    setInviterCode((prev) => (prev === '' ? urlInviterCode || '' : prev))
  }, [location.search])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const inviterCodeCookie =
      Cookies.get('inviter_code') || searchParams.get('inviter_code') || undefined
    if (oauthCode && oauthType === OauthType.Google) {
      setLoading(true)
      userStore.loginByOauth(oauthCode, 'google', inviterCodeCookie).finally(() => {
        setLoading(false)
      })
    }
  }, [oauthCode, oauthType])

  const _register = () => {
    switch (oauthType) {
      case OauthType.Google:
        _oauthByGoogle()
        break
      default:
        break
    }
  }

  const _oauthByGoogle = () => {
    setLoading(true)
    userStore
      .loginByOauth(oauthCode!, 'google', inviterCode)
      .then(() => {
        setStep(Step.Success)
      })
      .catch((e) => {
        //
        console.error(e)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return step === Step.SetPassword ? (
    <div className="flex flex-col items-center gap-8 md:px-6">
      <div className="card bg-panel-white w-[480px] flex flex-col items-start gap-4">
        <div
          className="w-12 h-12 icon-btn"
          onClick={() => {
            window.location.href = '/register'
          }}
        >
          <LeftArrow />
        </div>
        <div className="flex flex-col items-start gap-2 py-2">
          <div className="text-primary h5">
            <Trans i18nKey="register.googleTitleSetPassword">Set Password</Trans>
          </div>
          <div className="text-secondary t-lg">
            <Trans i18nKey="register.googleSubtitleSetPassword">
              Please set password and complete registration.
            </Trans>
          </div>
        </div>

        <div className="w-full t-lg text-primary">
          <div className="mb-2">
            <Trans i18nKey="login.labelPassword">Password</Trans>
          </div>
          <Input
            placeholder="Password"
            type="password"
            value={password}
            disabled={loading}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="w-full t-lg text-primary">
          <div className="mb-2">
            <Trans i18nKey="register.labelRePassword">Confirm Password</Trans>
          </div>
          <Input
            placeholder="Repeat password"
            type="password"
            value={rePassword}
            disabled={loading}
            onChange={(e) => setRePassword(e.target.value)}
          />
        </div>

        <Button type="secondary" block className="mt-2" disabled={loading} onClick={_register}>
          <Trans i18nKey="register.confirmBtn">Confirm</Trans>
        </Button>
      </div>
    </div>
  ) : step === Step.Success ? (
    <div className="flex flex-col items-center gap-8 mt-8 md:pb-20">
      <div className="card bg-panel-white w-[480px] flex flex-col items-center gap-4">
        <div className="py-2 flex flex-col items-center justify-center gap-4">
          <SuccessIcon className="w-16 h-16" />
          <div className="text-primary h5 text-center">
            <Trans i18nKey="register.title">Welcome To Glows.ai</Trans>
          </div>
          <div className="text-secondary t-lg text-center">
            <Trans i18nKey="register.googleSubtitleSuccess">
              Congratulations! Your account has been successfully created.
            </Trans>
          </div>
        </div>
        <Button
          block
          type="outline"
          className="flex items-center justify-center gap-2"
          onClick={() => {
            window.location.href = '/login'
          }}
        >
          <Trans i18nKey="register.login">Login to Glows.ai</Trans>
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-col items-center gap-y-8 mt-8 md:px-6 md:pb-20">
      <div className="card bg-panel-white w-[480px] md:w-full flex flex-col items-center gap-4">
        <div className="py-2 flex flex-col items-center gap-4">
          <LogoIcon className="w-16 h-16" />
          <h2 className="text-primary h5 text-center">
            <Trans i18nKey="register.title">Welcome To Glows.ai</Trans>
          </h2>
        </div>
        <GoogleLoginButton loading={loading} inviterCode={inviterCode} />
        <Button block type="outline" className="flex items-center justify-center gap-2" disabled>
          <GithubIcon className="w-6 h-6" />
          <Trans i18nKey="register.github">Continue with Github</Trans>
        </Button>

        <div className="w-full flex items-center mt-4 py-2 space-x-4">
          <div className="h-[1px] bg-[#E1E4EA] w-full"></div>
          <div className="text-tertiary text-nowrap t-sm">I Have an Invitation Code</div>
          <div className="h-[1px] bg-[#E1E4EA] w-full"></div>
        </div>

        <div className="w-full flex flex-col items-center">
          <input
            type="text"
            className="w-full text-center rounded-lg border border-[#E1E4EA] py-2 t-sm"
            placeholder="Enter invitation code"
            value={inviterCode}
            onChange={(e) => setInviterCode(e.target.value)}
          />
        </div>

        <div className="flex mt-4 t-lg md:t-sm gap-2">
          <Trans i18nKey="register.toLogin">
            Already have an account?
            <Link
              to={inviterCode === '' ? '/login' : `/login?inviter_code=${inviterCode}`}
              className="text-btn-secondary font-bold"
              onClick={() => {
                Cookies.set('inviter_code', inviterCode)
              }}
            >
              Login
            </Link>
          </Trans>
        </div>
      </div>
      <div className="max-w-[480px] text-center t-sm text-secondary">
        <Trans i18nKey="register.agreement">
          By registering on our platform means that you have read and agree to our
          <a className="text-primary" href="https://glows.ai/eula">
            EULA
          </a>
          ,
          <a className="text-primary" href="https://gata.net/terms-of-use">
            Terms of Service
          </a>
          and
          <a className="text-primary" href="https://gata.net/privacy-policy">
            Privacy Policy
          </a>
        </Trans>
      </div>
    </div>
  )
}
